import React, { useState, useEffect } from 'react';
import { Sparkles, Check, X, Loader2, Plus } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { auth } from '../utils/auth';
import { useTranslation } from 'react-i18next';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AI Time Suggestion - World Class Linear/Apple Quality
 */
const AITimeSuggestion = ({ caseId, onTimeAdded }) => {
  const { i18n } = useTranslation();
  const en = i18n.language === 'en' || i18n.language?.startsWith('en');
  
  const user = auth.getUser();
  const [loading, setLoading] = useState(true);
  const [suggestion, setSuggestion] = useState(null);
  const [noSuggestion, setNoSuggestion] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [hours, setHours] = useState(0);
  const [description, setDescription] = useState('');

  useEffect(() => {
    fetchSuggestion();
  }, [caseId]);

  const fetchSuggestion = async () => {
    try {
      const lang = en ? 'en' : 'no';
      const res = await axios.get(`${API}/ai/suggest-time/${caseId}?lawyer_id=${user.id}&language=${lang}`);
      
      if (res.data.has_suggestion) {
        setSuggestion(res.data.suggestion);
        setHours(res.data.suggestion.hours);
        setDescription(res.data.suggestion.description);
      } else {
        setNoSuggestion(true);
      }
    } catch (e) {
      setNoSuggestion(true);
    } finally {
      setLoading(false);
    }
  };

  const accept = async () => {
    setAccepting(true);
    try {
      const token = auth.getToken();
      await axios.post(
        `${API}/lexy/time-entries`,
        {
          case_id: caseId,
          user_id: user.id,
          hours: hours,
          description: description,
          date: new Date().toISOString().split('T')[0],
          billable: true,
          hourly_rate: suggestion?.hourly_rate || 1500
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(en ? 'Time logged' : 'Tid registrert');
      setSuggestion(null);
      setIsEditing(false);
      onTimeAdded?.();
    } catch (e) {
      toast.error(en ? 'Failed to log time' : 'Kunne ikke registrere');
    } finally {
      setAccepting(false);
    }
  };

  const startManual = () => {
    setSuggestion({ hours: 1, description: '' });
    setHours(1);
    setDescription('');
    setIsEditing(true);
    setNoSuggestion(false);
  };

  // Loading
  if (loading) {
    return (
      <div className="flex items-center gap-3 py-6 text-gray-400">
        <Loader2 className="w-4 h-4 animate-spin" />
        <span className="text-sm font-medium">{en ? 'Finding billable time...' : 'Finner fakturerbar tid...'}</span>
      </div>
    );
  }

  // No suggestion
  if (noSuggestion && !isEditing) {
    return (
      <button
        onClick={startManual}
        className="group flex items-center gap-3 py-4 text-gray-400 hover:text-gray-600 transition-colors"
      >
        <div className="w-8 h-8 rounded-full bg-gray-100 group-hover:bg-gray-200 flex items-center justify-center transition-colors">
          <Plus className="w-4 h-4" />
        </div>
        <span className="text-sm font-medium">{en ? 'Log time manually' : 'Registrer tid manuelt'}</span>
      </button>
    );
  }

  // Edit mode - Ultra minimal floating card
  if (isEditing) {
    return (
      <div className="relative mb-8">
        {/* Card */}
        <div 
          className="relative overflow-hidden rounded-[20px] bg-white"
          style={{
            boxShadow: '0 0 0 1px rgba(0,0,0,0.03), 0 2px 4px rgba(0,0,0,0.02), 0 12px 24px rgba(0,0,0,0.06)',
          }}
        >
          {/* Gradient accent line */}
          <div 
            className="absolute top-0 left-0 right-0 h-[3px]"
            style={{ background: 'linear-gradient(90deg, #6366f1, #8b5cf6, #a855f7)' }}
          />
          
          <div className="p-6 pt-7">
            {/* Hours input - Large and centered */}
            <div className="text-center mb-6">
              <div className="inline-flex items-baseline gap-2">
                <input
                  type="number"
                  step="0.25"
                  min="0.25"
                  value={hours}
                  onChange={(e) => setHours(parseFloat(e.target.value) || 0)}
                  className="w-24 text-[56px] font-light text-gray-900 text-center bg-transparent border-none focus:outline-none focus:ring-0 appearance-none"
                  style={{ 
                    fontFeatureSettings: '"tnum"',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", system-ui, sans-serif',
                    lineHeight: 1,
                  }}
                />
                <span className="text-xl text-gray-300 font-light">{en ? 'hours' : 'timer'}</span>
              </div>
            </div>
            
            {/* Description */}
            <div className="mb-6">
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={en ? 'What did you work on?' : 'Hva jobbet du med?'}
                className="w-full text-center text-[15px] text-gray-600 placeholder:text-gray-300 bg-transparent border-none focus:outline-none focus:ring-0"
                autoFocus
              />
            </div>
            
            {/* Actions */}
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={() => {
                  setIsEditing(false);
                  if (!suggestion?.hours) {
                    setSuggestion(null);
                    setNoSuggestion(true);
                  }
                }}
                className="h-10 px-5 text-sm font-medium text-gray-400 hover:text-gray-600 transition-colors"
              >
                {en ? 'Cancel' : 'Avbryt'}
              </button>
              <button
                onClick={accept}
                disabled={accepting || !description.trim() || hours <= 0}
                className="h-10 px-6 text-sm font-semibold text-white bg-gray-900 hover:bg-gray-800 disabled:bg-gray-200 disabled:text-gray-400 rounded-full transition-all flex items-center gap-2"
              >
                {accepting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Check className="w-4 h-4" />
                )}
                {en ? 'Log time' : 'Registrer'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Suggestion display - Elegant floating prompt
  return (
    <div className="relative mb-8">
      {/* Card */}
      <div 
        className="relative overflow-hidden rounded-[20px] bg-white group cursor-pointer"
        style={{
          boxShadow: '0 0 0 1px rgba(0,0,0,0.03), 0 2px 4px rgba(0,0,0,0.02), 0 12px 24px rgba(0,0,0,0.06)',
        }}
        onClick={() => setIsEditing(true)}
      >
        {/* Gradient accent line */}
        <div 
          className="absolute top-0 left-0 right-0 h-[3px]"
          style={{ background: 'linear-gradient(90deg, #6366f1, #8b5cf6, #a855f7)' }}
        />
        
        <div className="p-5 pt-6">
          {/* Top row - AI label */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-violet-500" />
              <span className="text-xs font-semibold text-violet-600 uppercase tracking-wider">
                {en ? 'AI Suggestion' : 'AI-forslag'}
              </span>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setSuggestion(null);
                setNoSuggestion(true);
              }}
              className="w-7 h-7 flex items-center justify-center text-gray-300 hover:text-gray-500 hover:bg-gray-100 rounded-full transition-all opacity-0 group-hover:opacity-100"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          
          {/* Main content */}
          <div className="flex items-center gap-6">
            {/* Hours - Big and beautiful */}
            <div className="flex items-baseline gap-1">
              <span 
                className="text-[48px] font-light text-gray-900 leading-none"
                style={{ 
                  fontFeatureSettings: '"tnum"',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", system-ui, sans-serif',
                }}
              >
                {suggestion?.hours}
              </span>
              <span className="text-lg text-gray-300 font-light">{en ? 'h' : 't'}</span>
            </div>
            
            {/* Description */}
            <div className="flex-1 min-w-0">
              <p className="text-[15px] text-gray-600 leading-relaxed line-clamp-2">
                {suggestion?.description}
              </p>
            </div>
            
            {/* Accept button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                accept();
              }}
              disabled={accepting}
              className="h-10 px-5 text-sm font-semibold text-white bg-gray-900 hover:bg-gray-800 rounded-full transition-all flex items-center gap-2 flex-shrink-0"
            >
              {accepting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  <span>{en ? 'Accept' : 'Godta'}</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
      
      {/* Subtle helper text */}
      <p className="text-center text-xs text-gray-300 mt-3">
        {en ? 'Click to edit before accepting' : 'Klikk for å redigere før du godtar'}
      </p>
    </div>
  );
};

export default AITimeSuggestion;
